import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface WizardFooterProps {
    /** Whether the current step is the final (Review) step, which submits. */
    isReviewStep: boolean;
    canGoBack: boolean;
    canAdvance: boolean;
    isSubmitting: boolean;
    submitDisabled: boolean;
    isSavingDraft: boolean;
    isSavingDraftSilent: boolean;
    isAttachmentBlocked: boolean;
    attachmentBlockedHint?: string;
    /** Why Submit is disabled (e.g. elastics enabled but incomplete). Shown next
     *  to the Submit button on the review step; navigation stays unblocked. */
    submitBlockedHint?: string;
    submitError: string | null;
    onBack: () => void;
    onSaveDraft: () => void;
    onNext: () => void;
    onSubmit: () => void;
}

/**
 * The wizard's bottom bar: Back, Save draft, and the terminal Next/Submit
 * action. Purely presentational — every gating decision is passed in.
 */
export function WizardFooter({
    isReviewStep,
    canGoBack,
    canAdvance,
    isSubmitting,
    submitDisabled,
    isSavingDraft,
    isSavingDraftSilent,
    isAttachmentBlocked,
    attachmentBlockedHint,
    submitBlockedHint,
    submitError,
    onBack,
    onSaveDraft,
    onNext,
    onSubmit
}: WizardFooterProps) {
    return (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 flex-shrink-0">
            <button
                onClick={onBack}
                disabled={!canGoBack}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
                <ChevronLeftIcon className="w-4 h-4" />
                Back
            </button>

            <div className="flex items-center gap-3">
                {isAttachmentBlocked && (
                    <span className="text-[11px] text-gray-400">{attachmentBlockedHint}</span>
                )}
                <button
                    onClick={onSaveDraft}
                    disabled={isSavingDraft || isSavingDraftSilent || isAttachmentBlocked}
                    title={attachmentBlockedHint}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSavingDraft && (
                        <div className="w-3 h-3 rounded-full border-2 border-yellow-500 border-t-transparent animate-spin" />
                    )}
                    Save draft
                </button>
                {submitError && <span className="text-xs text-red-500">{submitError}</span>}
                {isReviewStep && submitBlockedHint && (
                    <span className="text-[11px] text-gray-400">{submitBlockedHint}</span>
                )}
                {isReviewStep ? (
                    <button
                        onClick={onSubmit}
                        disabled={submitDisabled}
                        title={submitBlockedHint}
                        className="flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting && (
                            <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        )}
                        {isSubmitting ? 'Submitting…' : 'Submit'}
                    </button>
                ) : (
                    <button
                        onClick={onNext}
                        disabled={!canAdvance}
                        title={attachmentBlockedHint}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Next
                        <ChevronRightIcon className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}
